import { useEffect, useState, useRef } from "react";
import { Users } from "lucide-react";
import { connect } from "react-redux";
import { useLocation } from "react-router-dom";
import { createStructuredSelector } from "reselect";
import toast from "react-hot-toast";

import CommissionsTable from "../../components/commissions/commisions.component";
import { formatDate } from "../../components/distribution/utils";
import PageWrapper from "../../components/page-wrapper/page-wrapper.components";
import { selectCurrentDistro } from "../../redux/distro/distro.selector";
import { selectCurrentUser } from "../../redux/user/user.selectors";
import { getApiUrl } from "../../utils/api";
import { isClientOnlyForDistro } from "../../utils/firmAccess";
import { splitAdvisors } from "../../utils/advisors";
import PoliciesTable from "./policies-page";

const ClientsSection = ({ currentUser, currentDistro }) => {
    const [financialInfo, setFinancialInfo] = useState({
        commission: [],
        summary: [],
    });
    const [policies, setPolicies] = useState([]);
    const [isRmdBdd, setIsRmdBdd] = useState(false);
    const toastIdRef = useRef(null);
    const apiUrl = getApiUrl();

    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const clientOnly = isClientOnlyForDistro(currentUser, currentDistro);
    const clientType = clientOnly ? 'policy' : queryParams.get('type');

    const fetchData = async (startingDate, endingDate) => {
        toastIdRef.current = toast.loading('Loading clients...');
        try {
            const accessToken = currentUser.token;

            const resFinancials = clientOnly ? null : await fetch(
                `${apiUrl}/financials/${currentDistro}/${formatDate(startingDate)}/${formatDate(endingDate)}`,
                {
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                    },
                }
            );

            const resPolicies = await fetch(
                `${apiUrl}/financials/policies/${currentDistro}`,
                {
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                    },
                }
            );

            if (resFinancials?.status === 403 || resPolicies.status === 403) {
                toast.dismiss(toastIdRef.current);
                return;
            }

            if ((clientOnly || resFinancials.ok) && resPolicies.ok) {
                const financialData = clientOnly ? null : await resFinancials.json();
                const policiesData = await resPolicies.json();
                toast.dismiss(toastIdRef.current);
                if (financialData) setFinancialInfo(financialData);
                setPolicies(policiesData.uniquePolicies.filter(x => x.enrollmentStatus?.toLowerCase() !== 'pending'));
                setIsRmdBdd(policiesData.isRmdBdd);
            } else {
                toast.error('Failed to load clients', { id: toastIdRef.current });
                throw new Error(resFinancials?.statusText || resPolicies.statusText);
            }
        } catch (err) {
            toast.error('Failed to load clients', { id: toastIdRef.current });
        }
    };

    useEffect(() => {
        let defaultEndDate = new Date();
        defaultEndDate.setDate(1);
        defaultEndDate.setHours(-1);
        let defaultStarDate = new Date(defaultEndDate.getFullYear(), defaultEndDate.getMonth(), 1);
        if (currentDistro !== null) {
            fetchData(defaultStarDate, defaultEndDate);
        }
        return () => {
            if (toastIdRef.current) {
                toast.dismiss(toastIdRef.current);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentDistro, clientOnly]);

    return (
        <PageWrapper>
            <div className="flex-1 px-6 md:px-12 lg:pl-12 lg:pr-24 py-6 pb-5 overflow-y-auto">
                {/* page header with icon */}
                <div className="flex items-center gap-3 mb-6">
                    <div className="p-2 bg-primary/10 rounded-xl">
                        <Users className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                        <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
                            Clients
                        </h1>
                        <p className="text-sm text-gray-500">
                            Advisors ({new Set(policies.flatMap(p => splitAdvisors(p.advisor))).size}) · Policies ({policies.length}) · Employees ({policies.reduce((sum, p) => sum + (p.activeEmployees || 0), 0)})
                        </p>
                    </div>
                </div>

                {/* content */}
                <div>
                    {clientType === "policy" ? (
                        <PoliciesTable
                            isRmdBdd={isRmdBdd}
                            policies={policies}
                            distroId={currentDistro}
                            currentUser={currentUser}
                            apiUrl={apiUrl}
                        />
                    ) : (
                        <div className="bg-white rounded-2xl shadow-card p-6">
                            {financialInfo.commission.length > 0 ? (
                                <CommissionsTable commissions={financialInfo.commission} />
                            ) : (
                                <p className="text-gray-500 text-sm py-4 text-center">
                                    No commission data available.
                                </p>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </PageWrapper>
    );
};

const mapStateToProps = createStructuredSelector({
    currentUser: selectCurrentUser,
    currentDistro: selectCurrentDistro,
});

export default connect(mapStateToProps)(ClientsSection);
